import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../App';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../components/ui/collapsible';
import { 
  BarChart3, Clock, Users, Truck, MessageSquare, Receipt, 
  ClipboardList, Star, TrendingUp, TrendingDown, Calendar,
  ChevronDown, ChevronRight, RefreshCw, Filter, Download,
  CheckCircle, XCircle, AlertTriangle, Timer, Loader2, Search,
  FileText, Building2, ShieldCheck
} from 'lucide-react';
import RenovacionesTiemposReport from '../components/estadisticas/RenovacionesTiemposReport';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

export default function EstadisticasPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Filtros
  const [fechaInicio, setFechaInicio] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Datos
  const [dashboardData, setDashboardData] = useState(null);
  const [mensajeriaData, setMensajeriaData] = useState(null);
  const [ticketsData, setTicketsData] = useState(null);
  const [npsData, setNpsData] = useState(null);
  const [finanzasData, setFinanzasData] = useState({ cuentasPagar: null, cajaChica: null });
  const [requisicionesData, setRequisicionesData] = useState(null);
  const [renovacionesData, setRenovacionesData] = useState(null);
  
  // Estados para filtros de Renovaciones
  const [mesesVencimiento, setMesesVencimiento] = useState([]);
  const [filtroVenceEn, setFiltroVenceEn] = useState('todos');
  
  // Estados para filtros independientes de Finanzas
  const [fechaInicioCuentas, setFechaInicioCuentas] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [fechaFinCuentas, setFechaFinCuentas] = useState(() => new Date().toISOString().split('T')[0]);
  const [fechaInicioCaja, setFechaInicioCaja] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [fechaFinCaja, setFechaFinCaja] = useState(() => new Date().toISOString().split('T')[0]);
  const [reposicionesDisponibles, setReposicionesDisponibles] = useState([]);
  const [reposicionSeleccionada, setReposicionSeleccionada] = useState('todas');
  
  // Estados para filtros de NPS
  const [campanas, setCampanas] = useState([]); // Lista de campañas disponibles
  const [filtroEstadoCampana, setFiltroEstadoCampana] = useState('todas'); // todas, activas, cerradas
  const [campanaSeleccionada, setCampanaSeleccionada] = useState(null);
  const [busquedaCampana, setBusquedaCampana] = useState('');
  
  // Detalles expandibles
  const [detalleMensajeria, setDetalleMensajeria] = useState(null);
  const [detalleTickets, setDetalleTickets] = useState(null);
  const [detalleNps, setDetalleNps] = useState(null);
  
  // Estados de expandido
  const [expandedSections, setExpandedSections] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  // Función para verificar permisos de menú
  const hasMenuAccess = useCallback((menuId) => {
    // Admin siempre tiene acceso
    const userRole = user?.role?.toLowerCase();
    if (userRole === 'admin' || userRole === 'administrador') {
      return true;
    }
    
    if (!user?.menu_permissions) {
      return false;
    }
    
    const permission = user.menu_permissions[menuId];
    // Si el permiso está explícitamente denegado, no acceso
    if (permission?.access === false) {
      return false;
    }
    // Si el permiso está explícitamente otorgado, acceso permitido
    if (permission?.access === true) {
      return true;
    }
    // Por defecto, sin acceso
    return false;
  }, [user]);

  // Mapeo de tabs a permisos de menú
  const tabPermissions = useMemo(() => ({
    dashboard: true, // Dashboard siempre visible
    mensajeria: 'mensajeria',
    tickets: 'mesa_ayuda',
    nps: 'encuestas_nps',
    finanzas: 'finanzas_contabilidad',
    requisiciones: 'requisiciones',
    renovaciones: 'control_renovaciones'
  }), []);

  // Calcular qué tabs están disponibles según permisos
  const availableTabs = useMemo(() => {
    const tabs = [];
    
    // Dashboard siempre disponible
    tabs.push({ id: 'dashboard', label: 'Dashboard', icon: BarChart3 });
    
    // Mensajería
    if (hasMenuAccess('mensajeria')) {
      tabs.push({ id: 'mensajeria', label: 'Mensajería', icon: Truck });
    }
    
    // Mesa de Ayuda (Tickets)
    if (hasMenuAccess('mesa_ayuda')) {
      tabs.push({ id: 'tickets', label: 'Mesa de Ayuda', icon: MessageSquare });
    }
    
    // Encuestas NPS
    if (hasMenuAccess('encuestas_nps')) {
      tabs.push({ id: 'nps', label: 'Encuestas NPS', icon: Star });
    }
    
    // Finanzas y Contabilidad
    if (hasMenuAccess('finanzas_contabilidad')) {
      tabs.push({ id: 'finanzas', label: 'Finanzas', icon: Receipt });
    }
    
    // Requisiciones
    if (hasMenuAccess('requisiciones')) {
      tabs.push({ id: 'requisiciones', label: 'Requisiciones', icon: ClipboardList });
    }
    
    // Control de Renovaciones
    if (hasMenuAccess('control_renovaciones')) {
      tabs.push({ id: 'renovaciones', label: 'Renovaciones', icon: FileText });
    }
    
    return tabs;
  }, [hasMenuAccess]);

  // Verificar si el tab activo está disponible, si no cambiar al primero disponible
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(t => t.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/estadisticas/dashboard`, {
        headers,
        params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin }
      });
      setDashboardData(res.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [token, fechaInicio, fechaFin]);

  const fetchMensajeria = useCallback(async () => {
    try {
      const [resumen, porMensajero, porEjecutivo, tiempos] = await Promise.all([
        axios.get(`${API_URL}/api/estadisticas/mensajeria/resumen`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/mensajeria/por-mensajero`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/mensajeria/por-ejecutivo`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/mensajeria/tiempos`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } })
      ]);
      setMensajeriaData({
        resumen: resumen.data,
        porMensajero: porMensajero.data,
        porEjecutivo: porEjecutivo.data,
        tiempos: tiempos.data
      });
    } catch (error) {
      console.error('Error fetching mensajeria:', error);
    }
  }, [token, fechaInicio, fechaFin]);

  const fetchTickets = useCallback(async () => {
    try {
      const [resumen, porCategoria, porAsignado, porCreador, tiempos] = await Promise.all([
        axios.get(`${API_URL}/api/estadisticas/tickets/resumen`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/tickets/por-categoria`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/tickets/por-asignado`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/tickets/por-creador`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/tickets/tiempos`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } })
      ]);
      setTicketsData({
        resumen: resumen.data,
        porCategoria: porCategoria.data,
        porAsignado: porAsignado.data,
        porCreador: porCreador.data,
        tiempos: tiempos.data
      });
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  }, [token, fechaInicio, fechaFin]);

  // Cargar lista de campañas
  const fetchCampanas = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/encuestas/campanas`, { headers });
      setCampanas(res.data || []);
    } catch (error) {
      console.error('Error fetching campanas:', error);
    }
  }, [token]);

  const fetchNps = useCallback(async (campanaId = null) => {
    try {
      const params = { fecha_inicio: fechaInicio, fecha_fin: fechaFin };
      if (campanaId) {
        params.campana_id = campanaId;
      }
      
      const [resumen, porEjecutivo, porCampana] = await Promise.all([
        axios.get(`${API_URL}/api/estadisticas/encuestas/resumen`, { headers, params }),
        axios.get(`${API_URL}/api/estadisticas/encuestas/por-ejecutivo`, { headers, params }),
        axios.get(`${API_URL}/api/estadisticas/encuestas/por-campana`, { headers, params })
      ]);
      setNpsData({
        resumen: resumen.data,
        porEjecutivo: porEjecutivo.data,
        porCampana: porCampana.data
      });
    } catch (error) {
      console.error('Error fetching NPS:', error);
    }
  }, [token, fechaInicio, fechaFin]);

  // Fetch Cuentas por Pagar con filtro independiente
  const fetchCuentasPagar = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/estadisticas/cuentas-pagar/resumen`, { 
        headers, 
        params: { fecha_inicio: fechaInicioCuentas, fecha_fin: fechaFinCuentas } 
      });
      setFinanzasData(prev => ({ ...prev, cuentasPagar: res.data }));
    } catch (error) {
      console.error('Error fetching cuentas por pagar:', error);
    }
  }, [token, fechaInicioCuentas, fechaFinCuentas]);

  // Fetch Caja Chica con filtro independiente y reposición
  const fetchCajaChica = useCallback(async () => {
    try {
      const params = { 
        fecha_inicio: fechaInicioCaja, 
        fecha_fin: fechaFinCaja 
      };
      if (reposicionSeleccionada !== 'todas') {
        params.reposicion_id = reposicionSeleccionada;
      }
      const res = await axios.get(`${API_URL}/api/estadisticas/caja-chica/resumen`, { headers, params });
      setFinanzasData(prev => ({ ...prev, cajaChica: res.data }));
      // Guardar reposiciones disponibles
      if (res.data?.reposiciones_disponibles) {
        setReposicionesDisponibles(res.data.reposiciones_disponibles);
      }
    } catch (error) {
      console.error('Error fetching caja chica:', error);
    }
  }, [token, fechaInicioCaja, fechaFinCaja, reposicionSeleccionada]);

  const fetchFinanzas = useCallback(async () => {
    await Promise.all([fetchCuentasPagar(), fetchCajaChica()]);
  }, [fetchCuentasPagar, fetchCajaChica]);

  const fetchRequisiciones = useCallback(async () => {
    try {
      const [resumen, porDepartamento, porSolicitante] = await Promise.all([
        axios.get(`${API_URL}/api/estadisticas/requisiciones/resumen`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/requisiciones/por-departamento`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        axios.get(`${API_URL}/api/estadisticas/requisiciones/por-solicitante`, { headers, params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } })
      ]);
      setRequisicionesData({
        resumen: resumen.data,
        porDepartamento: porDepartamento.data,
        porSolicitante: porSolicitante.data
      });
    } catch (error) {
      console.error('Error fetching requisiciones:', error);
    }
  }, [token, fechaInicio, fechaFin]);

  // Fetch meses de vencimiento disponibles
  const fetchMesesVencimiento = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/estadisticas/renovaciones/meses-vencimiento`, { headers });
      setMesesVencimiento(response.data || []);
    } catch (error) {
      console.error('Error fetching meses vencimiento:', error);
    }
  }, [token]);

  const fetchRenovaciones = useCallback(async (venceEn = null) => {
    try {
      const venceParam = venceEn === 'todos' ? null : venceEn;
      const params = { 
        fecha_inicio: fechaInicio, 
        fecha_fin: fechaFin,
        ...(venceParam && { vence_en: venceParam })
      };
      
      const [resumen, porSucursal, porCompania, porRamo, porEjecutivo, porVencimiento] = await Promise.all([
        axios.get(`${API_URL}/api/estadisticas/renovaciones/resumen`, { headers, params }),
        axios.get(`${API_URL}/api/estadisticas/renovaciones/por-sucursal`, { headers, params }),
        axios.get(`${API_URL}/api/estadisticas/renovaciones/por-compania`, { headers, params }),
        axios.get(`${API_URL}/api/estadisticas/renovaciones/por-ramo`, { headers, params }),
        axios.get(`${API_URL}/api/estadisticas/renovaciones/por-ejecutivo`, { headers, params }),
        axios.get(`${API_URL}/api/estadisticas/renovaciones/por-vencimiento`, { headers, params })
      ]);
      setRenovacionesData({
        resumen: resumen.data,
        porSucursal: porSucursal.data,
        porCompania: porCompania.data,
        porRamo: porRamo.data,
        porEjecutivo: porEjecutivo.data,
        porVencimiento: porVencimiento.data
      });
    } catch (error) {
      console.error('Error fetching renovaciones:', error);
    }
  }, [token, fechaInicio, fechaFin]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchMensajeria(),
      fetchTickets(),
      fetchNps(campanaSeleccionada),
      fetchFinanzas(),
      fetchRequisiciones(),
      fetchRenovaciones(filtroVenceEn)
    ]);
    setLoading(false);
  }, [fetchDashboard, fetchMensajeria, fetchTickets, fetchNps, fetchFinanzas, fetchRequisiciones, fetchRenovaciones, campanaSeleccionada, filtroVenceEn]);

  useEffect(() => {
    fetchAllData();
    fetchCampanas();
    fetchMesesVencimiento();
  }, []);

  // Recargar NPS cuando cambia la campaña seleccionada
  useEffect(() => {
    if (campanaSeleccionada !== null) {
      fetchNps(campanaSeleccionada);
    }
  }, [campanaSeleccionada, fetchNps]);

  // Recargar renovaciones cuando cambia el filtro de vencimiento
  useEffect(() => {
    fetchRenovaciones(filtroVenceEn);
  }, [filtroVenceEn]);

  const handleApplyFilter = () => {
    fetchAllData();
  };

  // Filtrar campañas según estado y búsqueda
  const campanasFiltradas = useMemo(() => {
    return campanas.filter(c => {
      // Filtrar por estado
      if (filtroEstadoCampana === 'activas' && c.estado !== 'activa') return false;
      if (filtroEstadoCampana === 'cerradas' && c.estado !== 'cerrada' && c.estado !== 'finalizada') return false;
      
      // Filtrar por búsqueda
      if (busquedaCampana) {
        const search = busquedaCampana.toLowerCase();
        return c.nombre?.toLowerCase().includes(search) || 
               c.descripcion?.toLowerCase().includes(search);
      }
      return true;
    });
  }, [campanas, filtroEstadoCampana, busquedaCampana]);

  const handleSelectCampana = (campana) => {
    if (campanaSeleccionada === campana.id) {
      setCampanaSeleccionada(null); // Deseleccionar si ya está seleccionada
    } else {
      setCampanaSeleccionada(campana.id);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('es-DO');
  };

  const formatCurrency = (amount, currency = 'RD$') => {
    if (amount === null || amount === undefined) return `${currency} 0.00`;
    return `${currency} ${amount.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Formatear solo el número sin moneda
  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return amount.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatTime = (hours) => {
    if (!hours) return '0h';
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    if (hours < 24) return `${hours.toFixed(1)}h`;
    return `${(hours / 24).toFixed(1)}d`;
  };

  const getNpsColor = (score) => {
    if (score >= 50) return 'text-green-600';
    if (score >= 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600'
    };

    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">{title}</p>
              <p className="text-2xl font-bold">{value}</p>
              {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
            </div>
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon className="h-5 w-5" />
            </div>
          </div>
          {trend !== undefined && (
            <div className={`flex items-center mt-2 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              <span>{Math.abs(trend)}% vs período anterior</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const ExpandableTable = ({ title, data, columns, sectionKey }) => {
    const isExpanded = expandedSections[sectionKey];
    
    return (
      <Collapsible open={isExpanded} onOpenChange={() => toggleSection(sectionKey)}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-3 h-auto hover:bg-gray-50" data-testid={`expand-${sectionKey}`}>
            <span className="font-medium">{title}</span>
            <div className="flex items-center gap-2">
              {data?.length > 0 && <Badge variant="secondary" className="text-xs">{data.length}</Badge>}
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ScrollArea className="max-h-[400px] w-full">
            <div className="min-w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((col, i) => (
                      <TableHead key={i} className={`${col.className || ''} sticky top-0 bg-slate-50`}>{col.header}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.length > 0 ? data.map((row, i) => (
                    <TableRow key={i}>
                      {columns.map((col, j) => (
                        <TableCell key={j} className={col.cellClassName}>
                          {col.render ? col.render(row) : row[col.accessor]}
                        </TableCell>
                      ))}
                    </TableRow>
                  )) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center text-gray-500 py-4">
                      No hay datos disponibles
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="ml-0 md:ml-64 flex-1 p-6 overflow-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Cargando estadísticas...</span>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 overflow-auto">
        <div className="space-y-6" data-testid="estadisticas-page">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <BarChart3 className="h-7 w-7 text-blue-600" />
                Estadísticas
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Métricas consolidadas de todos los módulos del sistema
              </p>
            </div>
            
            {/* Filtros */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex items-center gap-2">
                <div>
                  <Label className="text-xs">Desde</Label>
                  <Input 
                    type="date" 
                    value={fechaInicio} 
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="w-36 h-9"
                    data-testid="fecha-inicio"
                  />
                </div>
                <div>
                  <Label className="text-xs">Hasta</Label>
                  <Input 
                    type="date" 
                    value={fechaFin} 
                    onChange={(e) => setFechaFin(e.target.value)}
                    className="w-36 h-9"
                    data-testid="fecha-fin"
                  />
                </div>
              </div>
              <Button onClick={handleApplyFilter} className="h-9" data-testid="aplicar-filtro">
                <Filter className="h-4 w-4 mr-1" />
                Aplicar
              </Button>
              <Button variant="outline" onClick={fetchAllData} className="h-9" data-testid="refresh">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex flex-wrap">
              {availableTabs.map(tab => (
                <TabsTrigger key={tab.id} value={tab.id} data-testid={`tab-${tab.id}`}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Mensajería - Solo si tiene permiso */}
            {hasMenuAccess('mensajeria') && (
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium">Mensajería</span>
                  </div>
                  <p className="text-2xl font-bold">{formatNumber(dashboardData?.mensajeria?.total)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant={dashboardData?.mensajeria?.tasa_completado >= 80 ? 'default' : 'secondary'} className="text-xs">
                      {dashboardData?.mensajeria?.tasa_completado || 0}% completado
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatNumber(dashboardData?.mensajeria?.completadas)} completadas</p>
                </CardContent>
              </Card>
            )}

            {/* Tickets - Solo si tiene permiso */}
            {hasMenuAccess('mesa_ayuda') && (
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">Mesa de Ayuda</span>
                  </div>
                  <p className="text-2xl font-bold">{formatNumber(dashboardData?.tickets?.total)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant={dashboardData?.tickets?.tasa_resolucion >= 80 ? 'default' : 'secondary'} className="text-xs">
                      {dashboardData?.tickets?.tasa_resolucion || 0}% resuelto
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatNumber(dashboardData?.tickets?.resueltos)} resueltos</p>
                </CardContent>
              </Card>
            )}

            {/* NPS - Solo si tiene permiso */}
            {hasMenuAccess('encuestas_nps') && (
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium">Encuestas NPS</span>
                  </div>
                  <p className={`text-2xl font-bold ${getNpsColor(dashboardData?.nps?.nps_score || 0)}`}>
                    {dashboardData?.nps?.nps_score || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">NPS Score</p>
                  <p className="text-xs text-gray-500">{formatNumber(dashboardData?.nps?.total_respuestas)} respuestas</p>
                </CardContent>
              </Card>
            )}

            {/* Cuentas por Pagar - Solo si tiene permiso */}
            {hasMenuAccess('finanzas_contabilidad') && (
              <Card className="border-l-4 border-l-orange-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium">Cuentas por Pagar</span>
                  </div>
                  <p className="text-2xl font-bold">{formatNumber(dashboardData?.cuentas_pagar?.total_facturas)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="destructive" className="text-xs">
                      {formatNumber(dashboardData?.cuentas_pagar?.pendientes)} pendientes
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Requisiciones - Solo si tiene permiso */}
            {hasMenuAccess('requisiciones') && (
              <Card className="border-l-4 border-l-cyan-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ClipboardList className="h-4 w-4 text-cyan-600" />
                    <span className="text-sm font-medium">Requisiciones</span>
                  </div>
                  <p className="text-2xl font-bold">{formatNumber(dashboardData?.requisiciones?.total)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-xs">
                      {formatNumber(dashboardData?.requisiciones?.pendientes)} pendientes
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Control de Renovaciones - Solo si tiene permiso */}
            {hasMenuAccess('control_renovaciones') && (
              <Card className="border-l-4 border-l-teal-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-teal-600" />
                    <span className="text-sm font-medium">Renovaciones</span>
                  </div>
                  <p className="text-2xl font-bold">{formatNumber(renovacionesData?.resumen?.total)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant={renovacionesData?.resumen?.tasa_renovacion >= 50 ? 'default' : 'secondary'} className="text-xs">
                      {renovacionesData?.resumen?.tasa_renovacion || 0}% renovadas
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatNumber(renovacionesData?.resumen?.pendientes)} pendientes</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* MENSAJERÍA TAB */}
        <TabsContent value="mensajeria">
          <div className="space-y-6">
            {/* Resumen */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <StatCard title="Total Solicitudes" value={formatNumber(mensajeriaData?.resumen?.total)} icon={Truck} color="blue" />
              <StatCard title="Completadas" value={formatNumber(mensajeriaData?.resumen?.completadas)} icon={CheckCircle} color="green" />
              <StatCard title="Pendientes" value={formatNumber(mensajeriaData?.resumen?.pendientes)} icon={Clock} color="yellow" />
              <StatCard title="En Proceso" value={formatNumber(mensajeriaData?.resumen?.en_proceso)} icon={Timer} color="blue" />
              <StatCard title="Devueltas" value={formatNumber(mensajeriaData?.resumen?.devueltas)} icon={XCircle} color="red" />
              <StatCard title="Urgentes" value={formatNumber(mensajeriaData?.resumen?.urgentes)} icon={AlertTriangle} color="orange" />
            </div>

            {/* Tiempos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Métricas de Tiempo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">
                      {formatTime(mensajeriaData?.tiempos?.tiempo_promedio_total_horas)}
                    </p>
                    <p className="text-xs text-gray-500">Tiempo Promedio Total</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {formatTime(mensajeriaData?.tiempos?.tiempo_promedio_mensajero_horas)}
                    </p>
                    <p className="text-xs text-gray-500">Tiempo Promedio Mensajero</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">
                      {formatTime(mensajeriaData?.tiempos?.tiempo_minimo_total_horas)}
                    </p>
                    <p className="text-xs text-gray-500">Tiempo Mínimo</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">
                      {formatTime(mensajeriaData?.tiempos?.tiempo_maximo_total_horas)}
                    </p>
                    <p className="text-xs text-gray-500">Tiempo Máximo</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tablas expandibles */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <ExpandableTable
                  title="Por Mensajero"
                  data={mensajeriaData?.porMensajero}
                  sectionKey="mensajeria-mensajero"
                  columns={[
                    { header: 'Mensajero', accessor: 'mensajero' },
                    { header: 'Asignadas', accessor: 'total_asignadas', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Entregadas', accessor: 'total_entregadas', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'T. Entrega', render: (row) => row.tiempo_promedio_entrega_horas ? `${row.tiempo_promedio_entrega_horas}h` : '-', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'T. Asig→Ent', render: (row) => row.tiempo_promedio_asignacion_entrega_horas ? `${row.tiempo_promedio_asignacion_entrega_horas}h` : '-', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Tasa', render: (row) => <Badge variant={row.tasa_entrega >= 80 ? 'default' : 'secondary'}>{row.tasa_entrega}%</Badge> }
                  ]}
                />
              </Card>

              <Card>
                <ExpandableTable
                  title="Por Ejecutivo"
                  data={mensajeriaData?.porEjecutivo}
                  sectionKey="mensajeria-ejecutivo"
                  columns={[
                    { header: 'Ejecutivo', accessor: 'ejecutivo' },
                    { header: 'Total', accessor: 'total', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Completadas', accessor: 'completadas', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Urgentes', accessor: 'urgentes', className: 'text-right', cellClassName: 'text-right' }
                  ]}
                />
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* TICKETS TAB */}
        <TabsContent value="tickets">
          <div className="space-y-6">
            {/* Resumen */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard title="Total Tickets" value={formatNumber(ticketsData?.resumen?.total)} icon={MessageSquare} color="blue" />
              <StatCard title="Abiertos" value={formatNumber(ticketsData?.resumen?.abiertos)} icon={Clock} color="yellow" />
              <StatCard title="En Progreso" value={formatNumber(ticketsData?.resumen?.en_proceso)} icon={Timer} color="blue" />
              <StatCard title="Pendientes" value={formatNumber(ticketsData?.resumen?.pendientes)} icon={AlertTriangle} color="orange" />
              <StatCard title="Resueltos" value={formatNumber(ticketsData?.resumen?.resueltos)} icon={CheckCircle} color="green" />
              <StatCard title="Cerrados" value={formatNumber(ticketsData?.resumen?.cerrados)} icon={XCircle} color="purple" />
            </div>

            {/* Tiempos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Métricas de Tiempo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">
                      {formatTime(ticketsData?.tiempos?.tiempo_promedio_resolucion_horas)}
                    </p>
                    <p className="text-xs text-gray-500">Tiempo Promedio Resolución</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">
                      {formatTime(ticketsData?.tiempos?.tiempo_promedio_abierto_horas)}
                    </p>
                    <p className="text-xs text-gray-500">Tiempo Promedio Abierto</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {formatTime(ticketsData?.tiempos?.tiempo_minimo_resolucion_horas)}
                    </p>
                    <p className="text-xs text-gray-500">Resolución Más Rápida</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                      {formatTime(ticketsData?.tiempos?.tiempo_maximo_resolucion_horas)}
                    </p>
                    <p className="text-xs text-gray-500">Resolución Más Lenta</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Por Prioridad */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Distribución por Prioridad</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <Badge variant="secondary" className="text-sm px-3 py-1">
                    Baja: {formatNumber(ticketsData?.resumen?.por_prioridad?.baja)}
                  </Badge>
                  <Badge className="text-sm px-3 py-1 bg-blue-100 text-blue-800">
                    Media: {formatNumber(ticketsData?.resumen?.por_prioridad?.media)}
                  </Badge>
                  <Badge className="text-sm px-3 py-1 bg-orange-100 text-orange-800">
                    Alta: {formatNumber(ticketsData?.resumen?.por_prioridad?.alta)}
                  </Badge>
                  <Badge variant="destructive" className="text-sm px-3 py-1">
                    Crítica: {formatNumber(ticketsData?.resumen?.por_prioridad?.critica)}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Tablas */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card>
                <ExpandableTable
                  title="Por Categoría"
                  data={ticketsData?.porCategoria}
                  sectionKey="tickets-categoria"
                  columns={[
                    { header: 'Categoría', accessor: 'categoria' },
                    { header: 'Total', accessor: 'total', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Tasa', render: (row) => <Badge variant={row.tasa_resolucion >= 80 ? 'default' : 'secondary'}>{row.tasa_resolucion}%</Badge> }
                  ]}
                />
              </Card>

              <Card>
                <ExpandableTable
                  title="Por Usuario Asignado"
                  data={ticketsData?.porAsignado}
                  sectionKey="tickets-asignado"
                  columns={[
                    { header: 'Usuario', accessor: 'asignado' },
                    { header: 'Asignados', accessor: 'total_asignados', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Tasa', render: (row) => <Badge variant={row.tasa_resolucion >= 80 ? 'default' : 'secondary'}>{row.tasa_resolucion}%</Badge> }
                  ]}
                />
              </Card>

              <Card>
                <ExpandableTable
                  title="Ejecutivos que más Asignan"
                  data={ticketsData?.porCreador}
                  sectionKey="tickets-creador"
                  columns={[
                    { header: 'Ejecutivo', accessor: 'ejecutivo' },
                    { header: 'Creados', accessor: 'total_creados', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Con Asignación', accessor: 'con_asignacion', className: 'text-right', cellClassName: 'text-right' }
                  ]}
                />
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* NPS TAB */}
        <TabsContent value="nps">
          <div className="space-y-6">
            {/* Filtros de Encuestas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Filtrar por Encuesta
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Filtros de estado y búsqueda */}
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="text-xs mb-1 block">Estado de Encuesta</Label>
                      <Select value={filtroEstadoCampana} onValueChange={setFiltroEstadoCampana}>
                        <SelectTrigger className="h-9" data-testid="filtro-estado-campana">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todas">Todas las Encuestas</SelectItem>
                          <SelectItem value="activas">Encuestas Activas</SelectItem>
                          <SelectItem value="cerradas">Encuestas Cerradas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1 min-w-[250px]">
                      <Label className="text-xs mb-1 block">Buscar Encuesta</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Buscar por nombre..."
                          value={busquedaCampana}
                          onChange={(e) => setBusquedaCampana(e.target.value)}
                          className="pl-9 h-9"
                          data-testid="busqueda-campana"
                        />
                      </div>
                    </div>
                    {campanaSeleccionada && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setCampanaSeleccionada(null);
                          fetchNps(null);
                        }}
                        className="h-9"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Limpiar Filtro
                      </Button>
                    )}
                  </div>
                  
                  {/* Lista de encuestas */}
                  <div className="border rounded-lg">
                    <ScrollArea className="h-[200px]">
                      <div className="p-2 space-y-1">
                        {campanasFiltradas.length === 0 ? (
                          <p className="text-center text-gray-500 py-4 text-sm">
                            No se encontraron encuestas
                          </p>
                        ) : (
                          campanasFiltradas.map((campana) => (
                            <div
                              key={campana.id}
                              onClick={() => handleSelectCampana(campana)}
                              className={`p-3 rounded-lg cursor-pointer transition-all ${
                                campanaSeleccionada === campana.id
                                  ? 'bg-blue-100 border-2 border-blue-500'
                                  : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                              }`}
                              data-testid={`campana-${campana.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{campana.nombre}</p>
                                  {campana.descripcion && (
                                    <p className="text-xs text-gray-500 truncate max-w-md">
                                      {campana.descripcion}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge 
                                    variant={campana.estado === 'activa' ? 'default' : 'secondary'}
                                    className={`text-xs ${
                                      campana.estado === 'activa' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {campana.estado === 'activa' ? 'Activa' : 'Cerrada'}
                                  </Badge>
                                  {campanaSeleccionada === campana.id && (
                                    <CheckCircle className="h-4 w-4 text-blue-600" />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                  
                  {campanaSeleccionada && (
                    <p className="text-xs text-blue-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Mostrando estadísticas de: <strong>{campanas.find(c => c.id === campanaSeleccionada)?.nombre}</strong>
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Resumen NPS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="col-span-2">
                <CardContent className="p-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-2">
                      NPS Score {campanaSeleccionada ? 'de Encuesta' : 'Global'}
                    </p>
                    <p className={`text-5xl font-bold ${getNpsColor(npsData?.resumen?.nps_score || 0)}`}>
                      {npsData?.resumen?.nps_score || 0}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      {npsData?.resumen?.total_respuestas || 0} respuestas de {npsData?.resumen?.total_enviados || 0} enviadas
                    </p>
                    <p className="text-xs text-gray-400">
                      Tasa de respuesta: {npsData?.resumen?.tasa_respuesta || 0}%
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-500 mb-1">Promotores (9-10)</p>
                  <p className="text-3xl font-bold text-green-600">{formatNumber(npsData?.resumen?.promotores)}</p>
                  <p className="text-xs text-gray-400">{npsData?.resumen?.porcentaje_promotores || 0}%</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-500 mb-1">Detractores (0-6)</p>
                  <p className="text-3xl font-bold text-red-600">{formatNumber(npsData?.resumen?.detractores)}</p>
                  <p className="text-xs text-gray-400">{npsData?.resumen?.porcentaje_detractores || 0}%</p>
                </CardContent>
              </Card>
            </div>

            {/* Distribución visual */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Distribución de Respuestas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center h-8 rounded-full overflow-hidden">
                  <div 
                    className="bg-green-500 h-full flex items-center justify-center text-xs text-white"
                    style={{ width: `${npsData?.resumen?.porcentaje_promotores || 0}%` }}
                  >
                    {npsData?.resumen?.porcentaje_promotores >= 10 && `${npsData?.resumen?.porcentaje_promotores}%`}
                  </div>
                  <div 
                    className="bg-yellow-500 h-full flex items-center justify-center text-xs text-white"
                    style={{ width: `${npsData?.resumen?.porcentaje_pasivos || 0}%` }}
                  >
                    {npsData?.resumen?.porcentaje_pasivos >= 10 && `${npsData?.resumen?.porcentaje_pasivos}%`}
                  </div>
                  <div 
                    className="bg-red-500 h-full flex items-center justify-center text-xs text-white"
                    style={{ width: `${npsData?.resumen?.porcentaje_detractores || 0}%` }}
                  >
                    {npsData?.resumen?.porcentaje_detractores >= 10 && `${npsData?.resumen?.porcentaje_detractores}%`}
                  </div>
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500"></span> Promotores</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-yellow-500"></span> Pasivos</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500"></span> Detractores</span>
                </div>
              </CardContent>
            </Card>

            {/* Tablas */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <ExpandableTable
                  title="NPS por Ejecutivo"
                  data={npsData?.porEjecutivo}
                  sectionKey="nps-ejecutivo"
                  columns={[
                    { header: 'Ejecutivo', accessor: 'ejecutivo' },
                    { header: 'NPS', render: (row) => <span className={`font-bold ${getNpsColor(row.nps_score)}`}>{row.nps_score}</span> },
                    { header: 'Respuestas', accessor: 'total', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Promedio', accessor: 'promedio', className: 'text-right', cellClassName: 'text-right' }
                  ]}
                />
              </Card>

              <Card>
                <ExpandableTable
                  title="NPS por Campaña"
                  data={npsData?.porCampana}
                  sectionKey="nps-campana"
                  columns={[
                    { header: 'Campaña', accessor: 'campana' },
                    { header: 'NPS', render: (row) => <span className={`font-bold ${getNpsColor(row.nps_score)}`}>{row.nps_score}</span> },
                    { header: 'Respuestas', accessor: 'total', className: 'text-right', cellClassName: 'text-right' }
                  ]}
                />
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* FINANZAS TAB */}
        <TabsContent value="finanzas">
          <div className="space-y-6">
            {/* Cuentas por Pagar */}
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Cuentas por Pagar
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Desde:</Label>
                      <Input 
                        type="date" 
                        value={fechaInicioCuentas} 
                        onChange={(e) => setFechaInicioCuentas(e.target.value)}
                        className="w-32 h-8 text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Hasta:</Label>
                      <Input 
                        type="date" 
                        value={fechaFinCuentas} 
                        onChange={(e) => setFechaFinCuentas(e.target.value)}
                        className="w-32 h-8 text-xs"
                      />
                    </div>
                    <Button size="sm" variant="outline" onClick={fetchCuentasPagar} className="h-8">
                      <Filter className="h-3 w-3 mr-1" />
                      Filtrar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold">{formatNumber(finanzasData?.cuentasPagar?.total_facturas)}</p>
                    <p className="text-xs text-gray-500">Total Facturas</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{formatNumber(finanzasData?.cuentasPagar?.pendientes)}</p>
                    <p className="text-xs text-gray-500">Pendientes</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{formatNumber(finanzasData?.cuentasPagar?.parciales)}</p>
                    <p className="text-xs text-gray-500">Parciales</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{formatNumber(finanzasData?.cuentasPagar?.pagadas)}</p>
                    <p className="text-xs text-gray-500">Pagadas</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{formatNumber(finanzasData?.cuentasPagar?.vencidas)}</p>
                    <p className="text-xs text-gray-500">Vencidas</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="py-2 px-3 text-left font-medium text-gray-600">Moneda</th>
                        <th className="py-2 px-3 text-right font-medium text-gray-600">Total</th>
                        <th className="py-2 px-3 text-right font-medium text-gray-600">Pagado</th>
                        <th className="py-2 px-3 text-right font-medium text-gray-600">Pendiente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['RD$', 'US$', 'EUR$'].map((moneda) => {
                        const total = finanzasData?.cuentasPagar?.totales_por_moneda?.[moneda] || 0;
                        const pagado = finanzasData?.cuentasPagar?.pagado_por_moneda?.[moneda] || 0;
                        const pendiente = finanzasData?.cuentasPagar?.pendiente_por_moneda?.[moneda] || 0;
                        
                        // Solo mostrar si hay algún valor
                        if (total === 0 && pagado === 0 && pendiente === 0) return null;
                        
                        return (
                          <tr key={moneda} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">{moneda}</td>
                            <td className="py-2 px-3 text-right">{formatAmount(total)}</td>
                            <td className="py-2 px-3 text-right text-green-600">{formatAmount(pagado)}</td>
                            <td className="py-2 px-3 text-right text-red-600">{formatAmount(pendiente)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Caja Chica */}
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Caja Chica
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Desde:</Label>
                      <Input 
                        type="date" 
                        value={fechaInicioCaja} 
                        onChange={(e) => setFechaInicioCaja(e.target.value)}
                        className="w-32 h-8 text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Hasta:</Label>
                      <Input 
                        type="date" 
                        value={fechaFinCaja} 
                        onChange={(e) => setFechaFinCaja(e.target.value)}
                        className="w-32 h-8 text-xs"
                      />
                    </div>
                    {reposicionesDisponibles.length > 0 && (
                      <Select value={reposicionSeleccionada} onValueChange={setReposicionSeleccionada}>
                        <SelectTrigger className="w-40 h-8 text-xs">
                          <SelectValue placeholder="Reposición" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todas">Todas las reposiciones</SelectItem>
                          {reposicionesDisponibles.map((rep) => (
                            <SelectItem key={rep.id} value={rep.id}>{rep.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button size="sm" variant="outline" onClick={fetchCajaChica} className="h-8">
                      <Filter className="h-3 w-3 mr-1" />
                      Filtrar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold">{formatNumber(finanzasData?.cajaChica?.total_gastos)}</p>
                    <p className="text-xs text-gray-500">Total Gastos</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(finanzasData?.cajaChica?.monto_total_gastos)}</p>
                    <p className="text-xs text-gray-500">Monto Gastos</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(finanzasData?.cajaChica?.monto_total_reposiciones)}</p>
                    <p className="text-xs text-gray-500">Reposiciones</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className={`text-2xl font-bold ${finanzasData?.cajaChica?.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(finanzasData?.cajaChica?.balance)}
                    </p>
                    <p className="text-xs text-gray-500">Balance</p>
                  </div>
                </div>

                {/* Por categoría */}
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Gastos por Categoría</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {finanzasData?.cajaChica?.por_categoria?.slice(0, 8).map((cat, i) => (
                      <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                        <span className="truncate">{cat.categoria}</span>
                        <Badge variant="outline">{formatCurrency(cat.monto)}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* REQUISICIONES TAB */}
        <TabsContent value="requisiciones">
          <div className="space-y-6">
            {/* Resumen */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard title="Total" value={formatNumber(requisicionesData?.resumen?.total)} icon={ClipboardList} color="blue" />
              <StatCard title="Pendientes" value={formatNumber(requisicionesData?.resumen?.pendientes)} icon={Clock} color="yellow" />
              <StatCard title="Aprobadas" value={formatNumber(requisicionesData?.resumen?.aprobadas)} icon={CheckCircle} color="green" />
              <StatCard title="Rechazadas" value={formatNumber(requisicionesData?.resumen?.rechazadas)} icon={XCircle} color="red" />
              <StatCard title="Entregadas" value={formatNumber(requisicionesData?.resumen?.entregadas)} icon={Truck} color="purple" />
              <StatCard title="Parciales" value={formatNumber(requisicionesData?.resumen?.parciales)} icon={AlertTriangle} color="orange" />
            </div>

            {/* Tiempos y Prioridades */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Tiempo Promedio de Aprobación</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center p-4">
                    <p className="text-4xl font-bold text-blue-600">
                      {formatTime(requisicionesData?.resumen?.tiempo_promedio_aprobacion_horas)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Tasa de aprobación: {requisicionesData?.resumen?.tasa_aprobacion || 0}%
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Distribución por Prioridad</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center p-2 bg-gray-100 rounded">
                      <p className="text-xl font-bold">{formatNumber(requisicionesData?.resumen?.por_prioridad?.baja)}</p>
                      <p className="text-xs">Baja</p>
                    </div>
                    <div className="text-center p-2 bg-blue-100 rounded">
                      <p className="text-xl font-bold text-blue-600">{formatNumber(requisicionesData?.resumen?.por_prioridad?.normal)}</p>
                      <p className="text-xs">Normal</p>
                    </div>
                    <div className="text-center p-2 bg-orange-100 rounded">
                      <p className="text-xl font-bold text-orange-600">{formatNumber(requisicionesData?.resumen?.por_prioridad?.alta)}</p>
                      <p className="text-xs">Alta</p>
                    </div>
                    <div className="text-center p-2 bg-red-100 rounded">
                      <p className="text-xl font-bold text-red-600">{formatNumber(requisicionesData?.resumen?.por_prioridad?.urgente)}</p>
                      <p className="text-xs">Urgente</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tablas */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <ExpandableTable
                  title="Por Departamento"
                  data={requisicionesData?.porDepartamento}
                  sectionKey="req-departamento"
                  columns={[
                    { header: 'Departamento', accessor: 'departamento' },
                    { header: 'Total', accessor: 'total', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Pendientes', accessor: 'pendientes', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Tasa', render: (row) => <Badge variant={row.tasa_aprobacion >= 80 ? 'default' : 'secondary'}>{row.tasa_aprobacion}%</Badge> }
                  ]}
                />
              </Card>

              <Card>
                <ExpandableTable
                  title="Por Solicitante"
                  data={requisicionesData?.porSolicitante}
                  sectionKey="req-solicitante"
                  columns={[
                    { header: 'Solicitante', accessor: 'solicitante' },
                    { header: 'Total', accessor: 'total', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Aprobadas', accessor: 'aprobadas', className: 'text-right', cellClassName: 'text-right' },
                    { header: 'Tasa', render: (row) => <Badge variant={row.tasa_aprobacion >= 80 ? 'default' : 'secondary'}>{row.tasa_aprobacion}%</Badge> }
                  ]}
                />
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* RENOVACIONES TAB */}
        <TabsContent value="renovaciones">
          <div className="space-y-6">
            {/* === Reporte de tiempos (Excel cliente) === */}
            <RenovacionesTiemposReport mesesVencimiento={mesesVencimiento} filtroVenceEn={filtroVenceEn} />
          </div>
        </TabsContent>
      </Tabs>
        </div>
      </main>
    </div>
  );
}
